import { usePrevious } from "@src/lib/hooks/usePrevious";
import { EditorContent, useEditor, Editor } from "@tiptap/react";
import type { EditorView } from "@tiptap/pm/view";
import { CSSProperties } from "react";
import React, { FC, useContext, useEffect, useMemo, useRef } from "react";
import type { SuggestionOptions } from "@tiptap/suggestion";
import useMentionConfig from "@src/features/libraryItem/course/build/comments/mentions/hooks/useMentionConfig";
import { TiptapEditorContext } from "@src/features/libraryItem/course/contexts/TiptapEditorContext";
import Text, { textVariants } from "@src/lib/ui/Text";
import { isRichTextEmpty } from "@src/features/libraryItem/course/utils/utils";
import { LinkEditor } from "./LinkEditor";
import { useSetBodyCursorType } from "@src/lib/contexts/BodyCursorTypeContext";
import { cn } from "@src/lib/utils/cn";
import useRichTextEditorExtensions from "../hooks/useRichTextEditorExtensions";
import { normalizeLatexDelimiters } from "@src/features/libraryItem/shared/normalizeLatexDelimiters";
import type { PlaceholderOptions } from "@tiptap/extension-placeholder";
import FormattingBubbleMenu from "@src/features/libraryItem/shared/FormattingBubbleMenu";
import LegacyFormattingBubbleMenu from "@src/features/libraryItem/shared/LegacyFormattingBubbleMenu";
import useBuilderCanvasTools from "@src/features/libraryItem/course/hooks/useBuilderCanvasTools";
import TableBubbleMenu from "@src/features/libraryItem/shared/TableBubbleMenu";
import { AnyExtension, Extension } from "@tiptap/core";
import {
  isMinimalHeadingActive,
  unboldMinimalHeading,
} from "./minimalHeadingsBold";

type Props = {
  text: string;
  hasEditContentPermission: boolean;
  onChange?: (text: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: PlaceholderOptions["placeholder"];
  // When true, placeholder is rendered on every empty node (not only the
  // focused one). Pair this with a function-based `placeholder` to show
  // different hints per block (e.g. a title hint on the first block).
  showPlaceholderOnEveryEmptyNode?: boolean;
  disabled?: boolean;
  styles?: CSSProperties;
  focusStyles?: CSSProperties;
  className?: string;
  editorParentClassName?: string;
  editorVariant?: "default" | "minimal-headings";
  autoFocus?: boolean;
  enableMentions?: boolean;
  mentionItems?: SuggestionOptions["items"];
  dataTestId?: string;
  transformPastedText?: (text: string) => string;
  disableTextVariants?: boolean;
  enableMathematics?: boolean;
  enableGapcursor?: boolean;
  enableImages?: boolean;
  fullWidth?: boolean;
  enableInlineFormatting?: boolean;
  disableTableHtml?: boolean;
  customLinkEditor?: React.ComponentType<{ editor: Editor }>;
  /** Replaces the selection pill (`FormattingBubbleMenu`) with the host's own. */
  customFormattingBubbleMenu?: React.ComponentType<{ editor: Editor }>;
  customImageBubbleMenu?: React.ComponentType<{ editor: Editor }>;
  /** Mounts the table bubble menu for good; `enabled` decides when it shows. */
  tableBubbleMenu?: { enabled: boolean };
  /** Added to the editor's own; keep the array stable (module-level). */
  extraExtensions?: AnyExtension[];
  handleKeyDown?: (view: EditorView, event: KeyboardEvent) => boolean | void;
};

// Minimal-headings variant: headings render like bolded paragraphs, so the
// only way Cmd/Ctrl+B can visibly unbold one is converting it to a paragraph.
const MinimalHeadingsBoldShortcut = Extension.create({
  name: "minimalHeadingsBoldShortcut",
  priority: 1000,
  addKeyboardShortcuts() {
    const handleBoldShortcut = () => {
      if (!isMinimalHeadingActive(this.editor)) return false;
      unboldMinimalHeading(this.editor);
      return true;
    };
    return {
      "Mod-b": handleBoldShortcut,
      "Mod-B": handleBoldShortcut,
    };
  },
});

const RichTextEditor: FC<Props> = ({
  text,
  hasEditContentPermission,
  onChange,
  onFocus = () => {},
  onBlur = () => {},
  placeholder = "Type here...",
  showPlaceholderOnEveryEmptyNode = false,
  disabled,
  styles: styleOverrides,
  focusStyles: focusStyleOverrides,
  className,
  editorParentClassName,
  autoFocus = false,
  enableMentions = false,
  mentionItems,
  dataTestId,
  transformPastedText,
  editorVariant = "default",
  disableTextVariants = false,
  enableMathematics = false,
  enableGapcursor = false,
  enableImages = true,
  fullWidth = false,
  enableInlineFormatting = false,
  disableTableHtml = false,
  customLinkEditor,
  customImageBubbleMenu,
  tableBubbleMenu,
  customFormattingBubbleMenu,
  extraExtensions,
  handleKeyDown,
}) => {
  const canvasTools = useBuilderCanvasTools();
  // Math-enabled surfaces normalize legacy $-delimited LaTeX so it renders
  // via the \(...\) extension regex; dollar amounts are left untouched.
  const textWithNewLines = enableMathematics
    ? normalizeLatexDelimiters(text)
    : text;
  const handleKeyDownRef = useRef(handleKeyDown);
  useEffect(() => {
    handleKeyDownRef.current = handleKeyDown;
  }, [handleKeyDown]);
  const setBodyCursorType = useSetBodyCursorType();
  const tiptapEditorContext = useContext(TiptapEditorContext);
  const defaultMentionsConfig = useMentionConfig(
    tiptapEditorContext?.editorRef,
  );
  const mentionsConfig = useMemo(
    () =>
      mentionItems
        ? { ...defaultMentionsConfig, items: mentionItems }
        : defaultMentionsConfig,
    [mentionItems, defaultMentionsConfig],
  );

  const baseExtensions = useRichTextEditorExtensions({
    placeholder,
    showPlaceholderOnEveryEmptyNode,
    enableMentions,
    mentionsConfig,
    enableMathematics,
    enableGapcursor,
    enableImages,
    enableImageResize: enableImages,
    disableTableHtml,
  });
  const extensions = useMemo(
    () => [
      ...baseExtensions,
      ...(editorVariant === "minimal-headings"
        ? [MinimalHeadingsBoldShortcut]
        : []),
      ...(extraExtensions ?? []),
    ],
    [baseExtensions, editorVariant, extraExtensions],
  );

  const editorEditable = !disabled && hasEditContentPermission;
  const editor = useEditor(
    {
      extensions,
      content: textWithNewLines,
      onUpdate({ editor }) {
        const html = editor.getHTML();
        if (textWithNewLines !== html) {
          if (isRichTextEmpty(html) && isRichTextEmpty(text)) return;
          onChange?.(html);
        }
      },
      parseOptions: {
        preserveWhitespace: true,
      },
      onFocus: () => {
        onFocus();
        tiptapEditorContext?.setEditorIsFocused(true);
        setBodyCursorType("text");
      },
      onBlur: () => {
        onBlur();
        tiptapEditorContext?.setEditorIsFocused(false);
        setBodyCursorType("auto");
      },
      onTransaction: ({ editor }) => {
        tiptapEditorContext?.formatting.setIsBold(editor.isActive("bold"));
        tiptapEditorContext?.formatting.setIsItalic(editor.isActive("italic"));
        tiptapEditorContext?.formatting.setIsBulletList(
          editor.isActive("bulletList"),
        );
        tiptapEditorContext?.formatting.setIsOrderedList(
          editor.isActive("orderedList"),
        );
        tiptapEditorContext?.formatting.setIsTable(editor.isActive("table"));
        tiptapEditorContext?.formatting.setIsHeading1(
          editor.isActive("heading", { level: 1 }),
        );
        tiptapEditorContext?.formatting.setIsHeading2(
          editor.isActive("heading", { level: 2 }),
        );
        tiptapEditorContext?.formatting.setIsHeading3(
          editor.isActive("heading", { level: 3 }),
        );
        tiptapEditorContext?.formatting.setIsParagraph(
          editor.isActive("paragraph"),
        );
      },
      editable: editorEditable,
      autofocus: autoFocus,
      editorProps: {
        handleKeyDown: (view, event) =>
          handleKeyDownRef.current?.(view, event) ?? false,
        transformPastedText: (text) => {
          // First apply any custom transform
          let transformedText = transformPastedText
            ? transformPastedText(text)
            : text;

          // If table HTML is disabled, strip table HTML
          if (disableTableHtml) {
            transformedText = transformedText.replace(
              /<table[^>]*>[\s\S]*?<\/table>/gi,
              "",
            );
          }

          return transformedText;
        },
      },
    },
    [editorEditable],
  );

  const prevText = usePrevious(textWithNewLines);

  useEffect(() => {
    if (!editor) return;
    if (
      prevText !== textWithNewLines &&
      textWithNewLines !== editor.getHTML() &&
      !editor.isFocused
    ) {
      const { from, to } = editor.state.selection;
      editor.commands.setContent(textWithNewLines);
      const docSize = editor.state.doc.content.size;
      editor.commands.setTextSelection({
        from: Math.min(from, docSize),
        to: Math.min(to, docSize),
      });
    }
  }, [textWithNewLines, prevText, editor]);

  if (!tiptapEditorContext) {
    return <Text type={"P2"}>Please initialize RichTextEditorContext</Text>;
  }

  return (
    <div className={cn("self-stretch break-words", editorParentClassName)}>
      <div className={cn("self-stretch break-words", fullWidth && "w-full")}>
        <EditorContent
          editor={editor}
          spellCheck
          className={cn(
            "flex max-w-full flex-1 border-none p-0",
            !disableTextVariants &&
              textVariants({ variant: "training-content" }),
            className,
            "ProseMirror",
            editorVariant === "minimal-headings" &&
              "ProseMirror-minimal-headings",
          )}
          style={
            editor?.isFocused && focusStyleOverrides
              ? { ...styleOverrides, ...focusStyleOverrides }
              : styleOverrides
          }
          ref={tiptapEditorContext?.editorRef}
          disabled={!editorEditable}
          placeholder={
            typeof placeholder === "string" ? placeholder : undefined
          }
          data-testid={dataTestId || "rich-text-editor"}
        />
        {editor && (
          <div>
            {customLinkEditor ? (
              React.createElement(customLinkEditor, { editor })
            ) : (
              <LinkEditor editor={editor} />
            )}
            {customFormattingBubbleMenu
              ? React.createElement(customFormattingBubbleMenu, { editor })
              : enableInlineFormatting &&
                (canvasTools ? (
                  <FormattingBubbleMenu editor={editor} />
                ) : (
                  <LegacyFormattingBubbleMenu editor={editor} />
                ))}
            {(enableInlineFormatting || tableBubbleMenu) && (
              <TableBubbleMenu
                editor={editor}
                enabled={tableBubbleMenu?.enabled ?? true}
              />
            )}
            {customImageBubbleMenu &&
              React.createElement(customImageBubbleMenu, { editor })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RichTextEditor;
